/**
 * Token amounts.
 *
 * Every amount the platform stores, signs or sends on-chain is an integer in the token's base
 * unit, such as 1 = 0.000001 USDC. People read and type amounts in whole tokens, such as
 * "100,000.50 USDC". These functions convert between the two exactly, without floating point.
 */

/**
 * Parses an amount typed in whole tokens into base units.
 *
 * Accepts digits with an optional decimal part and optional thousands separators (commas).
 * Refuses more decimal places than the token has, instead of rounding them away.
 *
 * @throws RangeError for anything that is not such an amount.
 * @example parseTokenAmount("100,000.5", 6) // 100_000_500_000n
 */
export function parseTokenAmount(text: string, decimals: number): bigint {
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!match) {
    throw new RangeError(`Not an amount: "${text}"`);
  }
  const whole = (match[1] ?? "").replaceAll(",", "");
  const fraction = match[2] ?? "";
  if (fraction.length > decimals) {
    throw new RangeError(`At most ${decimals} decimal places are possible.`);
  }
  return BigInt(whole) * 10n ** BigInt(decimals) + BigInt(fraction.padEnd(decimals, "0") || "0");
}

/**
 * Formats base units as whole tokens with thousands separators, without trailing zeros.
 *
 * @example formatTokenAmount(100_000_500_000n, 6) // "100,000.5"
 */
export function formatTokenAmount(units: bigint, decimals: number): string {
  const scale = 10n ** BigInt(decimals);
  const whole = (units / scale).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = (units % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}
